import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { orange } from '@mui/material/colors'
import { useCameraStore } from '@modugen/ui-toolkit'
import { subscribeAIChatSceneEvent, unsubscribeAIChatSceneEvent } from '@ui/events'
import type { AIChatSceneEvent } from '@ui/events'
import { useControlStore, useModelStore } from '@editorStores'
import { useFocusElements } from '@editorHooks'
import useUIStore from 'src/state/stores/ui'

// The elements a hovered link points at, in a colour apart from the marked ones.
const HOVER_COLOR = orange[700]
const NO_GUIDS: string[] = []

/**
 * Applies what the AI chat wants the scene to show (`AIChatSceneEvent`,
 * published by the navbar chat, outside this context-scoped control store):
 * a storey in the 2D floorplan view with its background plan, as the storey
 * toolbar's "2D" button does, a freshly uploaded IFC model, or -- while an
 * element link in an answer is hovered -- outlines of the planar elements it
 * points at (`useFocusElements`, orange). The elements the agent marked
 * (`mark_elements`) or the user clicked in the chat, kept in the UI store,
 * are outlined in blue as a layer of their own, so a hovered link shows next
 * to the marking. Only events of this editor's project apply.
 *
 * A subscription to a document event: the outside world this effect exists for.
 */
const AIChatSceneBridge = (): null => {
  const { projectId } = useParams()
  const setFloorplanStorey = useControlStore(state => state.setFloorplanStorey)
  const setShowGltfModel = useControlStore(state => state.setShowGltfModel)
  const setActiveIfcFileGuid = useControlStore(state => state.setActiveIfcFileGuid)
  const [focusedElementGuids, setFocusedElementGuids] = useState<string[]>([])
  const markedElementGuids = useUIStore(state =>
    projectId ? state.aiChatMarkedElementGuidsByProject[projectId] ?? NO_GUIDS : NO_GUIDS,
  )

  useFocusElements(markedElementGuids)
  // Above the marking where both outline the same element.
  useFocusElements(focusedElementGuids, { color: HOVER_COLOR, renderOrder: 1001 })

  useEffect(() => {
    const handleSceneEvent = ({ detail }: AIChatSceneEvent) => {
      if (detail.projectGuid !== projectId) return
      if (detail.focusedElementGuids !== undefined) {
        setFocusedElementGuids(detail.focusedElementGuids)
        return
      }
      if (detail.glbFileGuid !== undefined) {
        setShowGltfModel(true)
        setActiveIfcFileGuid(detail.glbFileGuid)
      }
      if (detail.storey !== undefined) {
        useModelStore.getState().setVisibleStoreys([detail.storey])
        setFloorplanStorey(detail.storey)
        useCameraStore.getState().setIsOrthographic(true)
      }
    }

    subscribeAIChatSceneEvent(handleSceneEvent)
    return () => {
      unsubscribeAIChatSceneEvent(handleSceneEvent)
    }
  }, [projectId, setFloorplanStorey, setShowGltfModel, setActiveIfcFileGuid])

  return null
}

export default AIChatSceneBridge
